/* eslint no-shadow: ['error', { 'allow': ['state', 'getters'] }] */
import axios from 'axios';
import { fcoUrl } from 'fcoModules/utilities';
import {
    getAttributeData,
    isProductOutOfStock,
    validatePartTypeId,
    getResultDetailsObj,
    getLocalQtyDetails,
    determineKitProductByAddQuoteResponse,
    buildResultsDetailsObj,
    getResultSummaryData,
    getResultDetailsData,
    normalizeParentPartTypeId,
} from 'fcoModules/usageTrackingUtils';
import {
    saveRelatedProductsLookup,
    clearAll,
    createIDBObjects,
    saveLookups,
    getAllUtLookupsAndProduct,
    generateUUIDObject,
    generateTimeStamp,
} from '../../services/usageTrackingService';
import { LOOKUP_TYPE } from '../../constants/usageTracking';

const UT_UUID = 'ut_uuid';
const UT_TIMESTAMP = 'ut_timestamp';

export const state = {
    isDisabled: false,
    uuid: null,
    utLookup: null,
    utLookupProducts: null,
    staticData: null,
};

export const getters = {
    getCurrentDate({ uuid }) {
        return uuid?.timeStamp;
    },
    getCurrentUUID({ uuid }) {
        return uuid?.uuid;
    },
    getCustomerId(state, getters, rootState) {
        return rootState.currentShop?.accountNumber;
    },
    getCustomerZipCode(state, getters, rootState) {
        return rootState.currentShop?.zip;
    },
    getCatalogVersion(state, getters, rootState) {
        return rootState.version?.ocatVersion;
    },
    getApplicationId({ staticData }) {
        return staticData?.application?.id;
    },
    getTokenParam({ staticData }) {
        return `?token=${staticData.application.authorization}`;
    },
    getStoreId(state, getters, rootState) {
        return rootState.currentShop?.homeStore?.storeId;
    },
    getCatalogLookupType({ utLookup }) {
        return (uuid) => utLookup.find((lp) => lp.uuid === uuid)?.lookupType ?? 'DRILL_DOWN';
    },
    getUUIDByPartId({ utLookupProducts, utLookup }) {
        return (partId) => {
            // It's possible make one or more lookups for the same product, so we'll get the last lookup for that product
            const productUUIDs = utLookupProducts.filter((p) => p.partId === partId).map((p) => p.uuid);

            const lookup = utLookup
                .filter((l) => productUUIDs.includes(l.uuid))
                .sort((a, b) => new Date(b.timeStamp) - new Date(a.timeStamp))
                .at(0);

            return lookup?.uuid;
        };
    },
    getUUIDByLegacyPartId({ utLookupProducts, utLookup }) {
        return (legacyPartId) => {
            // It's possible make one or more lookups for the same product, so we'll get the last lookup for that product
            const productUUIDs = utLookupProducts.filter((p) => p.legacyPartId === legacyPartId).map((p) => p.uuid);

            const lookup = utLookup
                .filter((l) => productUUIDs.includes(l.uuid))
                .sort((a, b) => new Date(b.timeStamp) - new Date(a.timeStamp))
                .at(0);

            return lookup?.uuid;
        };
    },
    getVehicleLookupType(state, getters, rootState) {
        const { currentVehicle } = rootState.vehicleSelector;
        if (!currentVehicle?.id) {
            return 'NULL';
        }
        if (currentVehicle?.vin?.length > 0) {
            return 'VIN';
        }

        return 'YMM';
    },
    getVehicleBaseId(state, getters, rootState) {
        return rootState.vehicleSelector?.currentVehicle?.vehicleId;
    },
    getVehicleId(state, getters, rootState) {
        return rootState.vehicleSelector?.currentVehicle?.id;
    },
    getSelectedPartTypes(state, getters, rootState) {
        const { lookupParts } = rootState.partSelection;
        return lookupParts?.map((lp) => ({ partTypeId: lp.partTypeId, partTypeName: lp.description })) ?? [];
    },
    lookupParentPartType({ utLookupProducts }) {
        return (partTypeId) => {
            const lookupProduct = utLookupProducts.find((utlp) => utlp.partTypeId === partTypeId || utlp.lookupPartTypeId === partTypeId);

            return normalizeParentPartTypeId(lookupProduct?.parentPartTypeId);
        };
    },
    getLocalQuantityOnHand(_, getters) {
        return (partId, vehicleId) => {
            const tempVehicleId = vehicleId || getters.getVehicleId;
            const part = getters.getVehicleProducts(tempVehicleId)?.find((p) => p.partId === partId)?.localQuantityOnHand;

            return part ?? 0;
        };
    },
    getIsStockingItem(_, getters) {
        return (partId, vehicleId) => {
            const tempVehicleId = vehicleId || getters.getVehicleId;
            const part = getters.getVehicleProducts(tempVehicleId)?.find((p) => p.partId === partId)?.stocking;

            return part ?? false;
        };
    },
    isAlreadyTracked({ utLookup }) {
        return (uuid) => {
            for (const l of utLookup) {
                if (l.uuid === uuid) {
                    return true;
                }
            }
            return false;
        };
    },
    /**
     * Returns result summary lookup object that describes part types that were returned in lookup process
     * @param {string} catalogLookupType - optional overriding parameter for specifying a different lookup type
     * @returns {Array}
     */
    getLookupSummaryData(_, getters) {
        return (catalogLookupType, selectedPartType) => {
            const results = [];
            let tempObj;
            let parentPartTypeId;

            if (getters.getCatalogLookupType(getters.getCurrentUUID) === 'PART_NO') {
                results.push({
                    partTypeId: 0,
                    parentPartTypeId: 0,
                });
            } else if (catalogLookupType && catalogLookupType === 'REL_ITEM') {
                results.push({
                    partTypeId: 0,
                    parentPartTypeId: 0,
                    vehicleId: 0,
                    vehicleLookupType: null,
                    catalogLookupType,
                });
            } else {
                const _selectedPartTypes = selectedPartType || getters.getSelectedPartTypes;
                for (let i = 0; i < _selectedPartTypes.length; i++) {
                    parentPartTypeId = getters.lookupParentPartType(_selectedPartTypes[i].partTypeId);
                    tempObj = {
                        partTypeId: validatePartTypeId(_selectedPartTypes[i].partTypeId),
                        parentPartTypeId,
                    };

                    if (catalogLookupType) {
                        tempObj.catalogLookupType = catalogLookupType;
                    }

                    results.push(tempObj);
                }
            }

            return results;
        };
    },
    /**
     * Gets specific lookup details that describe how product was looked up
     * @param {boolean} isRelatedProduct - true if lookup details are being gathered for related product actions/lookups
     * @param {Array} filteredBrands - contains list of currently filtered brands
     * @returns {Array} lookup details objects with required data to be logged
     */
    getLookupDetailsData(_, getters) {
        return ({ isRelatedProduct = false, filteredBrands = [], selectedManufacturer, searchQuery, lookupType, selectedPartTypes }) => {
            const _selectedPartTypes = selectedPartTypes || getters.getSelectedPartTypes;
            const results = [];
            const typeObj = {
                detailType: 'TYPE',
            };
            const vehicleLookupType = getters.getVehicleLookupType;
            const _lookupType = lookupType || getters.getCatalogLookupType(getters.getCurrentUUID);
            let i;

            if (vehicleLookupType === 'YMM') {
                typeObj.detailValue = 'app';
            } else if (vehicleLookupType === 'NULL') {
                typeObj.detailValue = 'all';
            } else if (vehicleLookupType === 'VIN') {
                typeObj.detailValue = 'vin';
            }

            if (_lookupType === 'PROMO') {
                results.push({
                    detailType: 'KIT',
                    detailValue: 'kit',
                });
            }

            // interchange overrides lookup type for this value
            if (_lookupType === 'INTCHANGE') {
                typeObj.detailValue = 'ic';

                results.push({
                    detailType: 'PART_NO',
                    detailValue: searchQuery,
                });

                for (i = 0; i < _selectedPartTypes.length; i++) {
                    results.push({
                        detailType: 'SELECTED_PART_TYPE',
                        detailValue: _selectedPartTypes[i].partTypeId,
                    });

                    results.push({
                        detailType: 'PART_NAME',
                        detailValue: _selectedPartTypes[i].partTypeName,
                    });
                }

                if (selectedManufacturer) {
                    results.push({
                        detailType: 'MANUFACTURER',
                        detailValue: selectedManufacturer,
                    });

                    // Supposedly this is always to be hard coded to this value for RWD
                    // when selecting a mfg during interchange search
                    results.push({
                        detailType: 'LINES',
                        detailValue: 'All Lines',
                    });
                }
            }

            if (isRelatedProduct) {
                typeObj.detailValue = 'all';
            }

            // BRD says to always have at least 1 lookup detail with detailType being "TYPE"
            results.push(typeObj);

            if (!isRelatedProduct) {
                if (_lookupType === 'PART_NO') {
                    results.push({
                        detailType: 'PART_NO',
                        detailValue: searchQuery,
                        custom: 'Y',
                    });
                } else if (_lookupType === 'DRILL_DOWN' || _lookupType === 'JOB') {
                    for (i = 0; i < _selectedPartTypes.length; i++) {
                        results.push({
                            detailType: 'SELECTED_PART_TYPE',
                            detailValue: _selectedPartTypes[i].partTypeId,
                            custom: 'Y',
                        });

                        results.push({
                            detailType: 'PART_NAME',
                            detailValue: _selectedPartTypes[i].partTypeName || '',
                            custom: 'Y',
                        });

                        // check to see if user searched a term that resulted in having filteredBrands
                        if (filteredBrands.length > 0 && searchQuery !== '') {
                            results.push({
                                detailType: 'MANUFACTURER',
                                detailValue: searchQuery,
                                custom: 'Y',
                            });
                        }
                    }
                }
            }

            return results;
        };
    },
    assembleLookupDetails(_, getters) {
        return (detailsData, uuid) => {
            const lookUpDetailsData = [];

            for (let i = 0; i < detailsData.length; i++) {
                lookUpDetailsData.push({
                    key: {
                        uuid: uuid || getters.getCurrentUUID,
                        detailType: detailsData[i].detailType,
                        servicingStoreNumber: getters.getStoreId,
                    },
                    detailValue: detailsData[i].detailValue,
                    custom: 'Y',
                });
            }
            return lookUpDetailsData;
        };
    },
    assembleLookupAttributes(_, getters) {
        return (attributeData, uuid) => {
            const lookUpAttributeData = [];

            for (let i = 0; i < attributeData.length; i++) {
                lookUpAttributeData.push({
                    key: {
                        attributeTypeId: attributeData[i].attributeTypeID,
                        attributeId: attributeData[i].attributeID,
                        attributeValueId: attributeData[i].attributeValueID,
                        uuid: uuid || getters.getCurrentUUID,
                        servicingStoreNumber: getters.getStoreId,
                    },
                    attributeState: 'ANSWERED',
                    partTypeId: null,
                });
            }

            return lookUpAttributeData;
        };
    },
    assembleResultsSummary(_, getters) {
        return (data, uuid, timeStamp) => {
            const results = [];

            for (let i = 0; i < data.length; i++) {
                results.push({
                    key: {
                        uuid: uuid || getters.getCurrentUUID,
                        servicingStoreNumber: getters.getStoreId,
                        date: timeStamp || getters.getCurrentDate,
                        partTypeId: data[i].partTypeId,
                    },
                    productsReturned: data[i].countProducts,
                    stockingProductsReturned: data[i].countStocking,
                    nonStockProductsReturned: data[i].countNonStocking,
                    specialOrderProductsReturned: data[i].countSpecialOrder,
                    outOfStockProductsReturned: data[i].countOutOfStock,
                    countDisplayed: data[i].countDisplayed,
                    duration: 0,
                });
            }

            return results;
        };
    },
    assembleResultsDetails(_, getters) {
        return (data, isActionItem, uuid, timeStamp, vehicleId) => {
            const results = [];
            let finalUUID;
            let partId;
            let isStockingItem;

            if (uuid) {
                finalUUID = uuid;
            } else if (isActionItem && data && data.length > 0) {
                const products = getters.getVehicleProducts(vehicleId);
                const uuidProductKey = `${data[0].brandCode}|${data[0].catalogItemId}`;
                const uuidProducts = products.find((p) => p.partId === uuidProductKey)?.uuid;
                if (uuidProducts) {
                    finalUUID = uuidProducts;
                }
            }

            if (!finalUUID) {
                finalUUID = getters.getCurrentUUID;
            }

            for (let i = 0; i < data.length; i++) {
                partId = `${data[i].brandCode}|${data[i].catalogItemId}`;
                isStockingItem = data[i].stocking;
                if (isStockingItem === null || typeof isStockingItem === 'undefined') {
                    isStockingItem = getters.getIsStockingItem(partId, vehicleId);
                }
                results.push({
                    key: {
                        uuid: finalUUID,
                        servicingStoreNumber: getters.getStoreId,
                        partTypeId: data[i].partTypeId,
                        line: data[i].line,
                        item: data[i].item,
                        productState: data[i].productState,
                    },
                    date: timeStamp || getters.getCurrentDate,
                    fitmentNotes: '', // BRD says to always leave blank/empty string
                    quotedPrice: data[i].quotedPrice,
                    desiredQuantity: data[i].desiredQuantity,
                    localQOH: data[i].localQOH || getters.getLocalQuantityOnHand(partId, vehicleId),
                    hubQOH: data[i].hubQOH,
                    dcQOH: data[i].dcQOH,
                    stockingItem: isStockingItem,
                    onOrder: data[i].onOrder,
                    onBackOrder: data[i].onBackOrder,
                    brandCode: data[i].brandCode,
                });
            }

            return results;
        };
    },
    createCatalogLookup(_, getters) {
        return ({
            partTypes,
            attributes = [],
            isRelatedProduct = false,
            filteredBrands = [],
            selectedPartTypes,
            selectedManufacturer,
            searchQuery,
            lookupType,
        } = {}) => {
            const catalogLookupObj = {
                lookupSummary: getters.getLookupSummaryData(isRelatedProduct ? 'REL_ITEM' : undefined, selectedPartTypes),
                lookupDetails: getters.getLookupDetailsData({
                    selectedPartTypes,
                    isRelatedProduct,
                    filteredBrands,
                    selectedManufacturer,
                    searchQuery,
                    lookupType,
                }),
                lookupAttributes: attributes.map((attribute) =>
                    getAttributeData({
                        attributeType: attribute.attributeType,
                        attributeID: attribute.attributeId,
                        attributeValueID: attribute.valueId,
                    })
                ),
            };

            if (partTypes) {
                catalogLookupObj.resultsSummary = getResultSummaryData(partTypes);
                catalogLookupObj.resultsDetails = getResultDetailsData(partTypes, 'LOOKUP');
            }

            return catalogLookupObj;
        };
    },
    getVehicleProducts({ utLookupProducts, utLookup }) {
        return (vehicleId) => {
            const uuids = new Set(utLookup.filter((l) => l.vehicleID === vehicleId).map((l) => l.uuid));

            if (uuids.size === 0) {
                return [];
            }

            return utLookupProducts.filter((lp) => uuids.has(lp.uuid));
        };
    },
    assembleLookupSummary(_, getters) {
        return (summaryData, uuid, timeStamp) => {
            const lookUpSummaryData = [];
            let vehicleId;
            let vehicleLookupType;

            for (let i = 0; i < summaryData.length; i++) {
                if (summaryData[i].vehicleId !== null) {
                    if (typeof summaryData[i].vehicleId === 'undefined') {
                        vehicleId = getters.getVehicleBaseId;
                    } else {
                        ({ vehicleId } = summaryData[i]);
                    }
                }

                if (summaryData[i].vehicleLookupType !== null) {
                    if (typeof summaryData[i].vehicleLookupType === 'undefined') {
                        vehicleLookupType = getters.getVehicleLookupType;
                    } else {
                        ({ vehicleLookupType } = summaryData[i]);
                    }
                } else {
                    vehicleLookupType = 'NULL';
                }

                const finalUUID = uuid || getters.getCurrentUUID;

                lookUpSummaryData.push({
                    key: {
                        date: timeStamp || getters.getCurrentDate,
                        application: getters.getApplicationId,
                        partTypeId: summaryData[i].partTypeId,
                        uuid: finalUUID,
                        servicingStoreNumber: getters.getStoreId,
                    },
                    parentPartTypeId: getters.lookupParentPartType(summaryData[i].partTypeId),
                    catalogLookupType: summaryData[i].catalogLookupType || getters.getCatalogLookupType(finalUUID),
                    vehicleLookupType,
                    vehicleId,
                    customerId: getters.getCustomerId,
                    customerZipCode: getters.getCustomerZipCode,
                    catalogVersion: getters.getCatalogVersion,
                });
            }

            return lookUpSummaryData;
        };
    },
};

export const mutations = {
    updateLookupsAndProducts(state, { utLookup, utLookupProducts }) {
        state.utLookup = [...state.utLookup, utLookup];
        state.utLookupProducts = [...state.utLookupProducts, ...utLookupProducts];
    },
    disableUsageTracking(state) {
        state.isDisabled = true;
        state.utLookup = null;
        state.utLookupProducts = null;
        state.uuid = null;
        sessionStorage.removeItem(UT_UUID);
        sessionStorage.removeItem(UT_TIMESTAMP);
        clearAll();
    },
    setUsageTrackingData(state, { utLookup, utLookupProducts }) {
        state.utLookup = utLookup;
        state.utLookupProducts = utLookupProducts;
    },
    resetUsageTrackingData(state) {
        state.isDisabled = false;
        state.utLookup = null;
        state.utLookupProducts = null;
        state.uuid = null;
        sessionStorage.removeItem(UT_UUID);
        sessionStorage.removeItem(UT_TIMESTAMP);
        clearAll();
    },
    setCurrentUUID(state, { uuid, timeStamp }) {
        state.uuid = { uuid, timeStamp };
    },
    setStaticData(state, staticData) {
        state.staticData = {
            application: {
                authorization: staticData.usageTrackingAuth,
                id: staticData.usageTrackingApplicationId,
            },
            paths: {
                lookupSummaryPath: staticData.usageTrackingLookupSummaryPath,
                lookupDetailsPath: staticData.usageTrackingLookupDetailsPath,
                lookupAttributesPath: staticData.usageTrackingLookupAttributesPath,
                resultsSummaryPath: staticData.usageTrackingResultsSummaryPath,
                resultsDetailsPath: staticData.usageTrackingResultsDetailsPath,
            },
        };
    },
};

const createUsageTrackingAction = (fn) => async (ctx, payload) => {
    try {
        const { state, dispatch } = ctx;

        await dispatch('loadUsageTracking');
        if (state.isDisabled) return;

        // Catch for errors from async functions
        return fn(ctx, payload)?.catch(() => {});
    } catch {
        // Catch for errors from sync functions
    }
};

let loadUsageTrackingCachedRequest;

export const actions = {
    async startCatalogLookup({ dispatch }) {
        const uuid = await dispatch('requestNewUUID');
        sessionStorage.setItem(UT_UUID, uuid.uuid);
        sessionStorage.setItem(UT_TIMESTAMP, uuid.timeStamp);
    },
    clearLookups: ({ commit }) => {
        commit('resetUsageTrackingData');
        clearAll();
    },
    async loadUsageTracking(ctx, isForceReload) {
        const { commit, state, dispatch, rootGetters } = ctx;
        const { isDisabled, utLookup, utLookupProducts } = state;

        // no need to do anything if disabled or we already have data
        if (isDisabled || utLookup || utLookupProducts) return;

        try {
            await dispatch('requestIfIdle', ['getUser', 'getFeatures', 'getCompany'], { root: true });
            const { isFeatureDisabled, isDemo, isTeamMember } = rootGetters;

            // check feature flag, or disable for team members and demo shops
            if (isFeatureDisabled('usage_tracking') || isTeamMember || isDemo) {
                commit('disableUsageTracking');
                return;
            }

            if (isForceReload || !loadUsageTrackingCachedRequest) {
                loadUsageTrackingCachedRequest = getAllUtLookupsAndProduct();
            }

            const utData = await loadUsageTrackingCachedRequest;

            commit('setUsageTrackingData', utData);
            commit('setCurrentUUID', {
                uuid: sessionStorage.getItem(UT_UUID),
                timeStamp: sessionStorage.getItem(UT_TIMESTAMP),
            });
        } catch {
            commit('disableUsageTracking');
        }
    },
    resetUsageTrackingData({ commit, dispatch }) {
        commit('resetUsageTrackingData');
        dispatch('loadUsageTracking', true);
    },
    requestNewUUID({ commit }) {
        const data = generateUUIDObject();
        commit('setCurrentUUID', data);

        return data;
    },
    logLookupSummary({ state, getters }, lookUpSummaryData) {
        if (state.isDisabled) return;

        navigator.sendBeacon(state.staticData.paths.lookupSummaryPath + getters.getTokenParam, JSON.stringify(lookUpSummaryData));
    },
    logLookupDetails({ state, getters }, lookupDetailsData) {
        if (state.isDisabled) return;

        navigator.sendBeacon(state.staticData.paths.lookupDetailsPath + getters.getTokenParam, JSON.stringify(lookupDetailsData));
    },
    logLookupAttributes({ state, getters }, lookUpAttributesData) {
        if (state.isDisabled) return;

        navigator.sendBeacon(state.staticData.paths.lookupAttributesPath + getters.getTokenParam, JSON.stringify(lookUpAttributesData));
    },
    logResultsSummary({ state, getters }, resultsSummary) {
        if (state.isDisabled) return;

        navigator.sendBeacon(state.staticData.paths.resultsSummaryPath + getters.getTokenParam, JSON.stringify(resultsSummary));
    },
    logResultsDetails({ state, getters }, resultsDetails) {
        if (state.isDisabled) return;

        navigator.sendBeacon(state.staticData.paths.resultsDetailsPath + getters.getTokenParam, JSON.stringify(resultsDetails));
    },
    logCatalogLookup({ state, getters, dispatch }, { data, optionalUUID }) {
        if (!state.isDisabled) {
            const finalUUID = optionalUUID || getters.getCurrentUUID;

            sessionStorage.setItem(UT_UUID, finalUUID);

            if (data?.lookupDetails?.length > 0) {
                dispatch('logLookupDetails', getters.assembleLookupDetails(data.lookupDetails, optionalUUID));
            }

            if (data?.lookupSummary?.length > 0) {
                dispatch('logLookupSummary', getters.assembleLookupSummary(data.lookupSummary, optionalUUID));
            }

            if (data?.resultsSummary?.length > 0) {
                dispatch('logResultsSummary', getters.assembleResultsSummary(data.resultsSummary, optionalUUID));
            }

            if (data?.resultsDetails?.length > 0) {
                dispatch('logResultsDetails', getters.assembleResultsDetails(data.resultsDetails, false, optionalUUID));
            }

            if (data?.lookupAttributes?.length > 0) {
                dispatch('logLookupAttributes', getters.assembleLookupAttributes(data.lookupAttributes, optionalUUID));
            }
        }
        /* User performs a catalog lookup (Most Popular or All Part Types tabs) and views PLP for part type(s) selected.
            - LookupSummary (one per part type)X
            - LookupAttribute (if applicable)X
            - LookupDetails (log part types and vehicle selected, if applicable) X
            - ResultsSummary (one per part type returned)
            - ResultsDetails (one per IMASTER part returned)
            * */
    },
    async saveCatalogLookUp({ commit }, catalogLookupData) {
        const idbObjects = createIDBObjects(catalogLookupData);
        await saveLookups(idbObjects);
        commit('updateLookupsAndProducts', idbObjects);
    },
    // this is the function we make available to our app for logging catalog lookups
    // other internal UT service functions can either call this function or logCatalogLookup() directly (depending on the scenario)
    catalogLookup: createUsageTrackingAction(async (ctx, { catalogLookupData, autoStart = false }) => {
        const { dispatch, getters, rootState } = ctx;
        const { partTypes } = catalogLookupData;

        if (autoStart) {
            await dispatch('startCatalogLookup');
        } else if (getters.isAlreadyTracked(getters.getCurrentUUID)) {
            return;
        }

        await dispatch('saveCatalogLookUp', {
            uuid: getters.getCurrentUUID,
            timeStamp: getters.getCurrentDate,
            currentVehicle: rootState.vehicleSelector.currentVehicle,
            resultDetails: partTypes ? getResultDetailsData(partTypes, 'LOOKUP') : [],
            catalogLookupData: {
                ...catalogLookupData,
                selectedPartTypes: catalogLookupData.selectedPartTypes || getters.getSelectedPartTypes,
            },
        });

        return dispatch('logCatalogLookup', {
            data: getters.createCatalogLookup(catalogLookupData),
        });
    }),
    async logAddPartResultsDetails(ctx, { addProductResultsDetails, optionalUUID }) {
        const { state, getters, dispatch } = ctx;
        if (state.isDisabled) return;

        dispatch('logResultsDetails', getters.assembleResultsDetails([addProductResultsDetails], true, optionalUUID));
        /* User adds part to the Quote (from the PLP or PDP).
            - ResultsDetails ("addToInvc" type)
            * */
    },
    relatedPartTypesLookup: createUsageTrackingAction(async (ctx, { partTypes, vehicle }) => {
        try {
            const { dispatch, getters, commit } = ctx;
            const uuid = await dispatch('requestNewUUID');
            const newProducts = partTypes.flatMap(({ partTypeId, parentPartTypeId, completeProducts }) =>
                completeProducts.map((product) => {
                    const { stocking, localQOH } = getLocalQtyDetails(product);

                    return {
                        vehicleId: vehicle.id,
                        baseVehicleId: vehicle.vehicleId,
                        partId: `${product.catalogKey.groupId}|${product.catalogKey.itemId}`,
                        legacyPartId: `${product.legacyKey.groupId}|${product.legacyKey.itemId}`,
                        /**
                         * Need to hard code this to DRILL_DOWN because choosing a new related part type from
                         * related parts drawer is technically a drill down. We need this value to update correctly
                         * in case a user started with a PART_NO (search) lookup so global usage tracking property gets updated
                         * and future logging of related part types still logs things correctly without getting hung up on PART_NO checks
                         */
                        lookupType: LOOKUP_TYPE.DRILL_DOWN,
                        partTypeId,
                        parentPartTypeId: normalizeParentPartTypeId(parentPartTypeId),
                        uuid,
                        stocking,
                        localQuantityOnHand: localQOH,
                    };
                })
            );
            const idbObjects = await saveRelatedProductsLookup(newProducts);

            commit('updateLookupsAndProducts', idbObjects);

            await dispatch('logCatalogLookup', {
                data: getters.createCatalogLookup({ partTypes }),
                optionalUUID: getters.getCurrentUUID,
            });
        } catch {
            // do nothing for now
        }
    }),
    relatedProductsLookup: createUsageTrackingAction(async (ctx, lookupResults) => {
        try {
            const { getters, dispatch, commit } = ctx;
            const vehicleId = getters.getVehicleId;
            const baseVehicleId = getters.getVehicleBaseId;
            const data = {
                ...lookupResults,
                // set the unknown defaults for these, as these related products won't have this information
                partTypeId: 0,
                parentPartTypeId: null,
            };

            const uuid = await dispatch('requestNewUUID');
            const newProducts = data.completeProducts.map((product) => {
                const { stocking, localQOH } = getLocalQtyDetails(product);

                return {
                    vehicleId,
                    baseVehicleId,
                    partId: `${product.catalogKey.groupId}|${product.catalogKey.itemId}`,
                    legacyPartId: `${product.legacyKey.groupId}|${product.legacyKey.itemId}`,
                    lookupType: getters.getCatalogLookupType(uuid),
                    parentPartTypeId: null,
                    partTypeId: 0,
                    uuid,
                    stocking,
                    localQuantityOnHand: localQOH,
                };
            });
            const idbObjects = await saveRelatedProductsLookup(newProducts);

            commit('updateLookupsAndProducts', idbObjects);

            sessionStorage.setItem(
                'curRelatedPartLookup',
                JSON.stringify({
                    lookupData: getters.createCatalogLookup({ partTypes: [data], isRelatedProduct: true }),
                    uuid: uuid.uuid,
                    hasBeenLogged: false,
                })
            );
        } catch {
            // do nothing for now
        }
    }),
    addPartToQuote: createUsageTrackingAction(async ({ dispatch, getters }, { addedProductData, order: { onOrder, onBackOrder } }) => {
        const { brandCode, itemCost, itemNumber, itemOrigin, itemOriginDetails, itemQuantity, line, locations, partNumber } = addedProductData;
        const isRelatedPart = itemOrigin === 'CROSS_SELL_PR_PRODUCT';
        const curRelatedProductLookupData = isRelatedPart ? JSON.parse(sessionStorage.getItem('curRelatedPartLookup')) : {};
        let hubQOH = -1;
        let dcQOH = -1;
        let uuid;

        locations.forEach(({ quantityOnHand, locationType }) => {
            if (locationType === 'HUB') {
                hubQOH = quantityOnHand;
            } else if (locationType === 'DISTRIBUTION_CENTER') {
                dcQOH = quantityOnHand;
            }
        });

        const resultDetails = {
            partTypeId: validatePartTypeId(itemOriginDetails),
            line,
            item: itemNumber,
            catalogItemId: partNumber,
            fitmentNotes: '', // BRD says always leave this blank
            productState: 'ADD_TO_INVOICE',
            quotedPrice: itemCost,
            desiredQuantity: itemQuantity,
            hubQOH,
            dcQOH,
            onOrder,
            onBackOrder,
            brandCode,
        };

        if (isRelatedPart && !curRelatedProductLookupData.hasBeenLogged) {
            ({ uuid } = curRelatedProductLookupData);
            await dispatch('catalogLookup', {
                catalogLookupData: curRelatedProductLookupData.lookupData,
                optionalUUID: uuid,
            });
            curRelatedProductLookupData.hasBeenLogged = true;
            sessionStorage.setItem('curRelatedPartLookup', JSON.stringify(curRelatedProductLookupData));
        }

        try {
            uuid = uuid || getters.getUUIDByPartId(`${resultDetails.brandCode}|${resultDetails.catalogItemId}`);

            if (!uuid) {
                return;
            }

            await dispatch('logAddPartResultsDetails', {
                addProductResultsDetails: resultDetails,
                optionalUUID: uuid,
            });
        } catch {
            // Some error handling
        }
    }),
    addKitPartToQuote: createUsageTrackingAction(async ({ dispatch }, { addedProductData, kitData }) => {
        // this is specific to when we add kit wizard products to quote, it uses a mix of response data and data sent to add to quote function.
        let hubQOH = -1;
        let dcQOH = -1;
        let curLoc;
        let curQOH;

        for (let i = 0; i < addedProductData.locations.length; i++) {
            curLoc = addedProductData.locations[i];
            curQOH = curLoc.quantityOnHand >= 0 ? curLoc.quantityOnHand : 0;
            if (curLoc.locationType === 'HUB') {
                hubQOH = curQOH;
            } else if (curLoc.locationType === 'DISTRIBUTION_CENTER') {
                dcQOH = curQOH;
            }
        }

        const resultDetails = {
            partTypeId: kitData.partTypeId || addedProductData.itemOriginDetails,
            line: addedProductData.line,
            item: kitData.legacyKey.itemId,
            catalogItemId: kitData.catalogKey.itemId,
            fitmentNotes: '', // BRD says always leave this blank
            productState: 'ADD_TO_INVOICE',
            quotedPrice: kitData.itemCost,
            desiredQuantity: kitData.userSpecifiedQuantity,
            hubQOH,
            dcQOH,
            onOrder: kitData.partPriceAvailabilityResponse.onOrder,
            onBackOrder: kitData.partPriceAvailabilityResponse.onBackOrder,
            brandCode: addedProductData.brandCode,
        };

        try {
            await dispatch('logAddPartResultsDetails', { addProductResultsDetails: resultDetails });
        } catch {
            // Fail silently
        }
    }),
    addPrebuiltKitToQuote: createUsageTrackingAction((ctx, { addedProducts, kit, partTypeId }) => {
        try {
            const { dispatch, getters } = ctx;
            const selectedPartTypes = getters.getSelectedPartTypes;
            let parentPartTypeId;

            // first log new lookup summary for this part type id
            if (selectedPartTypes.length > 0) {
                // if selected part types is populated when looking up kits it is safe to select
                // one and only slot as users can only select one catalog lookup type when choosing kits
                parentPartTypeId = normalizeParentPartTypeId(selectedPartTypes[0].partTypeId);
            } else {
                parentPartTypeId = 0;
            }

            dispatch(
                'logLookupSummary',
                getters.assembleLookupSummary([
                    {
                        partTypeId,
                        parentPartTypeId,
                    },
                ])
            );

            addedProducts.forEach((addedProduct) => {
                // create specific object for usage tracking to consume
                const kitData = {
                    partType: {
                        kits: [kit],
                    },
                };

                // first determine which kitWizardProducts product matches our current response product
                const curPrebuiltKitProduct = determineKitProductByAddQuoteResponse(addedProduct, kitData);

                // should always have a product match but put this here just in case to prevent any js errors
                if (curPrebuiltKitProduct) {
                    dispatch('addKitPartToQuote', {
                        addedProductData: addedProduct,
                        kitData: curPrebuiltKitProduct,
                    });
                }
            });
        } catch (ex) {
            /*
            Ignore usage tracking errors for now, invalid usage tracking records are automatically
            scrubbed at the end of the day
            */
        }
    }),
    plpMoreInformation: createUsageTrackingAction((ctx, { qty, productState, partData, partTypeId, optionalUUID }) => {
        const { dispatch, getters } = ctx;
        const parentPartTypeId = getters.lookupParentPartType(partTypeId) ?? '0';
        const data = getResultDetailsObj(parentPartTypeId, qty, productState, partData, partTypeId);
        const finalUUID = optionalUUID || getters.getUUIDByPartId(`${data.brandCode}|${data.catalogItemId}`);

        // Bail out if there is no uuid associate with the product
        if (!finalUUID) {
            return;
        }

        dispatch('logResultsDetails', getters.assembleResultsDetails([data], true, optionalUUID));
        /* On the PLP, user clicks on a part image, part name, or the "More Information" link.
            - ResultsDetails ("vwDtl" type)
        * */
    }),
    removePartFromQuote: createUsageTrackingAction(async (ctx, { data, groupId, itemId }) => {
        try {
            const { dispatch, getters } = ctx;
            /* User removes part from Quote (from the Quote page or Mini-Quote).
            - ResultsDetails ("rmvFromInvc" type)
            * */
            const resp = await axios.get(fcoUrl(`/product/extraInfo?mfg=${groupId}&partNumber=${itemId}`));

            data.product.catalogKey = { itemId, groupId };
            data.product.legacyKey = resp.data;

            const resultsDetails = buildResultsDetailsObj(data.product, 'REM_FROM_INVOICE');

            const uuid = getters.getUUIDByLegacyPartId(`${groupId}|${itemId}`);
            const timeStamp = generateTimeStamp();

            dispatch('logResultsDetails', getters.assembleResultsDetails([resultsDetails], true, uuid, timeStamp, data.vehicleId));
        } catch {
            // Fail silently
        }
    }),
    viewPartFromQuote: createUsageTrackingAction(async (ctx, { data, line, itemNumber }) => {
        try {
            const { dispatch, getters } = ctx;
            const getCatalogKeyURL = fcoUrl(`/product/extraInfo?&sourceType=LEGACY&targetType=CATALOG&mfg=${line}&partNumber=${itemNumber}`);
            const { data: catalogKey } = await axios.get(getCatalogKeyURL);

            data.product.catalogKey = catalogKey;

            const uuid = getters.getUUIDByLegacyPartId(`${line}|${itemNumber}`);
            const timeStamp = generateTimeStamp();

            const resultsDetails = getters.assembleResultsDetails(
                [buildResultsDetailsObj(data.product, 'VW_DETAILS')],
                true,
                uuid,
                timeStamp,
                data.vehicleId
            );
            dispatch('logResultsDetails', resultsDetails);
        } catch {
            // Fail silently
        }
    }),
    quickAddPartsToQuote: createUsageTrackingAction(async (ctx, { products, isWorksheet, vehicleId }) => {
        const { dispatch, getters } = ctx;
        /* User selects parts on the Quick Order box, then clicks Go To Quote.
        - LookupSummary (one record)
        - LookupDetails (log part numbers entered)
        - ResultsSummary (one record with part type = 0)
        - ResultsDetails ("lookup" records for all IMASTER parts. "addToInvc" records for all parts.)
        * */
        const { uuid, timeStamp } = generateUUIDObject();
        const lookupType = isWorksheet ? 'QUICK_ADD' : 'PART_NO';
        const partTypes = [
            {
                completeProducts: products,
                parentPartTypeId: 0,
                partTypeId: 0,
            },
        ];
        await dispatch('saveCatalogLookUp', {
            uuid,
            timeStamp,
            currentVehicle: {
                id: vehicleId,
            },
            resultDetails: getResultDetailsData(partTypes, lookupType),
            catalogLookupData: {
                partTypes,
            },
        });

        const utObj = {
            lookupSummary: [
                {
                    partTypeId: 0,
                    parentPartTypeId: 0,
                    catalogLookupType: lookupType,
                    vehicleLookupType: null,
                    vehicleId: null,
                },
            ],
            lookupDetails: [
                {
                    detailType: 'TYPE',
                    detailValue: 'all',
                },
            ],
            resultsSummary: [],
            resultsDetails: [],
        };
        const stockingTotals = {
            countStocking: 0,
            countNonStocking: 0,
            countSpecialOrder: 0,
            countOutOfStock: 0,
        };
        let ppAvailability;
        let resultsDetailsAddToInvoice;

        for (let partIndex = 0; partIndex < products.length; partIndex++) {
            ppAvailability = products[partIndex].partPriceAvailabilityResponse;
            if (ppAvailability.stocking) {
                stockingTotals.countStocking += 1;
            }
            if (isProductOutOfStock(products[partIndex])) {
                stockingTotals.countOutOfStock += 1;
            }
            if (ppAvailability.nonStocking && ppAvailability.itemMaster) {
                stockingTotals.countNonStocking += 1;
            }
            if (!ppAvailability.itemMaster) {
                stockingTotals.countSpecialOrder += 1;
            }

            utObj.lookupDetails.push({
                detailType: 'PART_NO',
                detailValue: products[partIndex].catalogKey.itemId,
            });

            resultsDetailsAddToInvoice = buildResultsDetailsObj(products[partIndex], 'ADD_TO_INVOICE', true);
            utObj.resultsDetails.push(resultsDetailsAddToInvoice);
            if (ppAvailability.itemMaster) {
                utObj.resultsDetails.push(buildResultsDetailsObj(products[partIndex], 'LOOKUP', true));
            }
        }

        utObj.resultsSummary.push({
            partTypeId: 0,
            countProducts: products.length,
            countStocking: stockingTotals.countStocking,
            countNonStocking: stockingTotals.countNonStocking,
            countSpecialOrder: stockingTotals.countSpecialOrder,
            countOutOfStock: stockingTotals.countOutOfStock,

            // This countDisplayed value needs to be hardcoded for RWD
            countDisplayed: -1,
            duration: 0,
        });
        dispatch('logLookupDetails', getters.assembleLookupDetails(utObj.lookupDetails, uuid));
        dispatch('logLookupSummary', getters.assembleLookupSummary(utObj.lookupSummary, uuid, timeStamp));
        dispatch('logResultsSummary', getters.assembleResultsSummary(utObj.resultsSummary, uuid, timeStamp));
        dispatch('logResultsDetails', getters.assembleResultsDetails(utObj.resultsDetails, false, uuid, timeStamp, null));
    }),
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
